import { useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import {
  Button,
  TxButton,
  useGlobalModalContext,
  useTranslation,
  type TxButtonProps,
} from "rujira.ui";
import { useWalletAccounts } from "../../services/accounts";
import { useRedacted } from "../../services/redacted";
import { useRedactedAccountProvider } from "../../redacted/tx";
import { useRedactedV2 } from "../../redacted/v2";
import { PrivateFeeReview } from "./PrivateFeeReview";
import { PrivateOperationSteps } from "./PrivateOperationSteps";
import {
  PrivateToastErrorGhost,
  PrivateToastGhost,
  PrivateToastSuccessGhost,
} from "./PrivateToastGhost";
import {
  PrivateFeePlacementProvider,
  PrivateFeeRowSlot,
} from "./PrivateFeePlacement";
import "./PrivateActionButton.scss";
import { isPrivateClaim, isZeroFeePrivateClaim } from "./privateClaimFees";

type Props = TxButtonProps & {
  "data-tooltip-content"?: string | null;
};
type Identity = { wallet: string; subwallet?: string };

/** Table actions never quote or insert content into their tiny cells on render.
 * Claims (recognised from the calls they run) quote on click and skip the
 * modal only for a verified zero debit; other actions retain confirmation.
 * Countdown renders cannot replace the clicked intent, and a changed session
 * permanently invalidates that click. */
export const PrivateActionButton: FC<Props> = (props) => {
  const { showModal } = useGlobalModalContext();
  const accounts = useWalletAccounts();
  const v2 = useRedactedV2();
  const { isPrivateMode } = useRedacted();
  const identity = {
    wallet: accounts.selected?.address.address || "",
    subwallet: v2.selectedSubwallet,
  };
  const sessionKey = JSON.stringify([
    identity.wallet,
    identity.subwallet,
    v2.enabled,
    isPrivateMode,
    v2.status,
    !!props.disabled,
  ]);
  const session = useRef({ key: sessionKey, generation: 0 });
  if (session.current.key !== sessionKey)
    session.current = {
      key: sessionKey,
      generation: session.current.generation + 1,
    };
  const generation = session.current.generation;
  const title =
    props["aria-label"] ||
    props["data-tooltip-content"] ||
    (typeof props.label === "string" ? props.label : "Confirm private action");
  const disabled = props.disabled || !props.msg || v2.status !== "ready";
  const claim = useMemo(() => isPrivateClaim(props.msg), [props.msg]);
  const privateOperationInProgress =
    v2.operation?.phase === "preparing" ||
    v2.operation?.phase === "submitting" ||
    v2.operation?.phase === "pending";
  const openConfirmation = (transaction = props, error?: Error) => {
    showModal({
      className: "private-action-modal",
      backgroundClose: false,
      hideCloseButton: true,
      children: (
        <PrivateActionConfirmation
          transaction={transaction}
          title={title}
          identity={identity}
          error={error}
        />
      ),
    });
  };
  const assertCurrentSession = () => {
    if (
      disabled ||
      !v2.enabled ||
      !isPrivateMode ||
      !identity.wallet ||
      session.current.generation !== generation
    )
      throw new Error("Private account changed. Close and try again.");
  };
  if (claim) {
    return (
      <TxButton
        {...props}
        disabled={disabled || privateOperationInProgress}
        aria-label={title}
        ReviewComponent={undefined}
        hideSimulation
        simulateOnClick={(simulation, msg) => {
          assertCurrentSession();
          if (isZeroFeePrivateClaim(simulation)) return true;
          openConfirmation({ ...props, msg });
          return false;
        }}
        onClick={(event) => {
          assertCurrentSession();
          props.onClick?.(event);
        }}
        onError={(error) => {
          if (error?.type === "simulation")
            openConfirmation(
              { ...props, msg: error.msg },
              error.error instanceof Error
                ? error.error
                : new Error("Unable to verify the claim fee. Try again.")
            );
          props.onError?.(error);
        }}
      />
    );
  }
  return (
    <Button
      type="button"
      className={props.className}
      style={props.style}
      label={props.label}
      disabled={disabled || privateOperationInProgress}
      aria-label={title}
      aria-haspopup="dialog"
      data-tooltip-id="global-tip"
      data-tooltip-content={props["data-tooltip-content"]}
      onClick={() => {
        if (disabled || !accounts.selected) return;
        openConfirmation();
      }}>
      {props.children}
    </Button>
  );
};

const PrivateActionConfirmation: FC<{
  transaction: Props;
  title: string;
  identity: Identity;
  error?: Error;
}> = ({ transaction, title, identity, error }) => {
  const { hideModal } = useGlobalModalContext();
  const { t } = useTranslation("common");
  const accounts = useWalletAccounts();
  const accountProvider = useRedactedAccountProvider(accounts);
  const v2 = useRedactedV2();
  const { isPrivateMode } = useRedacted();
  const titleId = useId();
  const [submitting, setSubmitting] = useState(false);
  const invalidated = useRef(false);
  useEffect(() => {
    if (
      v2.operation?.phase === "confirmed" &&
      v2.operation.finalTransaction === false
    )
      setSubmitting(false);
  }, [v2.operation?.id, v2.operation?.phase, v2.operation?.finalTransaction]);
  // Never turn a stale private confirmation into a public operation, or reuse
  // it after switching wallet/session. The quote engine also binds its issued
  // capability to the exact intent, fees and current session generation.
  if (
    !v2.enabled ||
    !isPrivateMode ||
    !["ready", "processing"].includes(v2.status) ||
    accounts.selected?.address.address !== identity.wallet ||
    v2.selectedSubwallet !== identity.subwallet
  )
    invalidated.current = true;
  const busy = submitting || v2.status === "processing";
  const awaitingReceipt = v2.operation?.phase === "pending";
  const privateOperationInProgress =
    v2.operation?.phase === "preparing" ||
    v2.operation?.phase === "submitting" ||
    awaitingReceipt;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="modal__header">
        <h2 id={titleId}>{title}</h2>
      </div>
      <PrivateFeePlacementProvider>
        {invalidated.current ? (
          <p role="alert" className="color-orange text-center condensed">
            Private account changed. Close and try again.
          </p>
        ) : error ? (
          <p role="alert" className="color-orange text-center condensed">
            {error.message}
          </p>
        ) : (
          <PrivateFeeRowSlot className="condensed" />
        )}
        <div className="modal__footer px-3 py-2 flex ai-c jc-sb gap-1">
          <Button
            type="button"
            className="button--grey button--outline"
            label={awaitingReceipt ? t("close") : t("cancel")}
            disabled={busy && !awaitingReceipt}
            onClick={hideModal}
          />
          {!invalidated.current && !error && (
            <TxButton
              msg={transaction.msg}
              simulationScope={transaction.simulationScope}
              accountProvider={accountProvider}
              className="button"
              label={t("confirm")}
              autoFocus
              disabled={transaction.disabled}
              ReviewComponent={PrivateFeeReview}
              hideSimulation
              refreshSimulationOnError
              simulationPaused={
                v2.status !== "ready" || privateOperationInProgress
              }
              pendingTransaction={
                v2.operation && {
                  id: v2.operation.id,
                  status:
                    v2.operation.phase === "confirmed" ||
                    v2.operation.phase === "failed"
                      ? v2.operation.phase
                      : "pending",
                  txHash: v2.operation.txHash,
                  message: v2.operation.message,
                  finalTransaction: v2.operation.finalTransaction,
                }
              }
              onSimulation={transaction.onSimulation}
              toastOpts={{
                ...transaction.toastOpts,
                loading: transaction.toastOpts?.loading ?? (
                  <PrivateOperationSteps fallback="Submitting Transaction" />
                ),
                loadingIcon: transaction.toastOpts?.loadingIcon ?? (
                  <PrivateToastGhost />
                ),
                successIcon: transaction.toastOpts?.successIcon ?? (
                  <PrivateToastSuccessGhost />
                ),
                errorIcon: transaction.toastOpts?.errorIcon ?? (
                  <PrivateToastErrorGhost />
                ),
              }}
              onClick={(event) => {
                if (invalidated.current || v2.status !== "ready")
                  throw new Error(
                    "Private account changed. Close and try again."
                  );
                setSubmitting(true);
                transaction.onClick?.(event);
              }}
              onError={(error) => {
                setSubmitting(false);
                transaction.onError?.(error);
              }}
              onCancel={() => {
                // Undone before sending: the review stays open for another try.
                setSubmitting(false);
                transaction.onCancel?.();
              }}
              onSuccess={(result) => {
                setSubmitting(false);
                hideModal();
                return transaction.onSuccess?.(result);
              }}
            />
          )}
        </div>
      </PrivateFeePlacementProvider>
    </div>
  );
};
